import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import aiIcon from '../action-menu/icon--ai.svg';
import Box from '../box/box.jsx';
import ActionMenu from '../action-menu/action-menu.jsx';
import styles from './stage-selector.css';
import {isRtl} from 'scratch-l10n';
import BackdropCreator from '../ai-backdrop-creator/backdropCreator.jsx';

import backdropIcon from '../action-menu/icon--backdrop.svg';
import fileUploadIcon from '../action-menu/icon--file-upload.svg';
import paintIcon from '../action-menu/icon--paint.svg';
import surpriseIcon from '../action-menu/icon--surprise.svg';
import searchIcon from '../action-menu/icon--search.svg';

const messages = defineMessages({
    addBackdropFromLibrary: {
        id: 'gui.spriteSelector.addBackdropFromLibrary',
        description: 'Button to add a stage in the target pane from library',
        defaultMessage: 'Choose a Backdrop'
    },
    addBackdropFromAI: {
        id: 'gui.stageSelector.addBackdropFromAI',
        description: 'Button to create a backdrop with AI',
        defaultMessage: 'Create Backdrop with AI'
    },
    addBackdropFromPaint: {
        id: 'gui.stageSelector.addBackdropFromPaint',
        description: 'Button to add a stage in the target pane from paint',
        defaultMessage: 'Paint'
    },
    addBackdropFromSurprise: {
        id: 'gui.stageSelector.addBackdropFromSurprise',
        description: 'Button to add a random stage in the target pane',
        defaultMessage: 'Surprise'
    },
    addBackdropFromFile: {
        id: 'gui.stageSelector.addBackdropFromFile',
        description: 'Button to add a stage in the target pane from file',
        defaultMessage: 'Upload Backdrop'
    }
});

const StageSelector = props => {
    const [showAIBackdropCreator, setShowAIBackdropCreator] = useState(false);

    const {
        backdropCount,
        containerRef,
        dragOver,
        fileInputRef,
        selected,
        raised,
        receivedBlocks,
        url,
        onAIBackdropAdd,
        onBackdropFileUploadClick,
        onBackdropFileUpload,
        onClick,
        onPointerEnter,
        onPointerLeave,
        onNewBackdropClick,
        onSurpriseBackdropClick,
        onEmptyBackdropClick,
        ...componentProps
    } = props;

    const intl = useIntl();

    return (
        <Box
            className={classNames(styles.stageSelector, {
                [styles.isSelected]: selected,
                [styles.raised]: raised || dragOver,
                [styles.receivedBlocks]: receivedBlocks
            })}
            componentRef={containerRef}
            onClick={onClick}
            onPointerEnter={onPointerEnter}
            onPointerLeave={onPointerLeave}
            {...componentProps}
        >
            <div className={styles.header}>
                <div className={styles.headerTitle}>
                    <FormattedMessage
                        defaultMessage="Stage"
                        description="Label for the stage in the stage selector"
                        id="gui.stageSelector.stage"
                    />
                </div>
            </div>

            {url ? (
                <img
                    className={styles.costumeCanvas}
                    src={url}
                />
            ) : null}

            <div className={styles.label}>
                <FormattedMessage
                    defaultMessage="Backdrops"
                    description="Label for the backdrops in the stage selector"
                    id="gui.stageSelector.backdrops"
                />
            </div>

            <div className={styles.count}>{backdropCount}</div>

   <ActionMenu
    className={styles.addButton}
    img={backdropIcon}
    moreButtons={[
        {
            title: intl.formatMessage(messages.addBackdropFromAI),
            img: aiIcon,
            onClick: () => setShowAIBackdropCreator(true)
        },
        {
            title: intl.formatMessage(messages.addBackdropFromLibrary),
            img: backdropIcon,
            onClick: onNewBackdropClick
        },
        {
            title: intl.formatMessage(messages.addBackdropFromFile),
            img: fileUploadIcon,
            onClick: onBackdropFileUploadClick,
            fileAccept: '.svg, .png, .bmp, .jpg, .jpeg, .gif',
            fileChange: onBackdropFileUpload,
            fileInput: fileInputRef,
            fileMultiple: true
        },
        {
            title: intl.formatMessage(messages.addBackdropFromSurprise),
            img: surpriseIcon,
            onClick: onSurpriseBackdropClick
        },
        {
            title: intl.formatMessage(messages.addBackdropFromPaint),
            img: paintIcon,
            onClick: onEmptyBackdropClick
        }
    ]}
    title="Backdrops"
    tooltipPlace={isRtl(intl.locale) ? 'right' : 'left'}
    onClick={() => {}}
/>
            {showAIBackdropCreator && (
                <div
                    style={{
                        position: 'fixed',
                        zIndex: 1000,
                        top: '80px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'white',
                        borderRadius: '12px',
                        padding: '24px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                        width: 'calc(100% - 80px)',
                        maxWidth: '850px',
                        maxHeight: 'calc(100vh - 110px)',
                        overflowY: 'auto'
                    }}
                >
                  <BackdropCreator
                    onAddToProject={imageBase64 => {
                    console.log(
                    'AI backdrop: sending to Scratch importer',
                    imageBase64.length
                    );

                    onAIBackdropAdd(imageBase64);
                    setShowAIBackdropCreator(false);
                    }}
                    onClose={() => setShowAIBackdropCreator(false)}
                    />
                </div>
            )}
        </Box>
    );
};

StageSelector.propTypes = {
    backdropCount: PropTypes.number.isRequired,
    containerRef: PropTypes.func,
    dragOver: PropTypes.bool,
    fileInputRef: PropTypes.func,
    onAIBackdropAdd: PropTypes.func.isRequired,
    onBackdropFileUpload: PropTypes.func,
    onBackdropFileUploadClick: PropTypes.func,
    onClick: PropTypes.func,
    onEmptyBackdropClick: PropTypes.func,
    onPointerEnter: PropTypes.func,
    onPointerLeave: PropTypes.func,
    onNewBackdropClick: PropTypes.func,
    onSurpriseBackdropClick: PropTypes.func,
    raised: PropTypes.bool.isRequired,
    receivedBlocks: PropTypes.bool.isRequired,
    selected: PropTypes.bool.isRequired,
    url: PropTypes.string
};

export default StageSelector;